import {
  CheckCircleOutlined,
  GithubOutlined,
  GoogleOutlined,
  MailOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  message,
  Space,
  Typography,
} from 'antd'
import { type ReactNode, useState } from 'react'

import { unwrapApiResult } from '@/api/unwrapApiResult'
import { VODOPAD_ENDPOINT } from '@/constants/constants'
import { requestPasswordlessLogin } from '@/services/authApi'
import { authService } from '@/services/authService'

const { Title, Text } = Typography

type OAuthLoginMethod = 'google' | 'github'

const OAUTH_PROVIDERS: ReadonlyArray<{
  method: OAuthLoginMethod
  label: string
  icon: ReactNode
}> = [
  { method: 'google', label: 'Sign in with Google', icon: <GoogleOutlined /> },
  { method: 'github', label: 'Sign in with GitHub', icon: <GithubOutlined /> },
]

const callbackUrl = () => `${window.location.origin}/auth/callback`

const oauthRedirectUrl = (method: OAuthLoginMethod) => {
  const params = new URLSearchParams({ redirect_to: callbackUrl() })
  return `${VODOPAD_ENDPOINT}/auth/oauth/redirect/${method}?${params.toString()}`
}

function OAuthButtons({
  loading,
  onLogin,
}: {
  loading: boolean
  onLogin: (method: OAuthLoginMethod) => void
}) {
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {OAUTH_PROVIDERS.map(({ method, label, icon }) => (
        <Button
          key={method}
          icon={icon}
          size="large"
          block
          onClick={() => onLogin(method)}
          disabled={loading}
          data-testid={`login-${method}`}
        >
          {label}
        </Button>
      ))}
    </Space>
  )
}

function EmailLoginForm({
  loading,
  onSubmit,
}: {
  loading: boolean
  onSubmit: (email: string) => void
}) {
  const [form] = Form.useForm<{ email: string }>()

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={({ email }) => onSubmit(email.trim())}
    >
      <Form.Item
        name="email"
        validateTrigger="onSubmit"
        rules={[
          { required: true, message: 'Email is required' },
          { type: 'email', message: 'Invalid email format' },
        ]}
      >
        <Input
          prefix={<MailOutlined />}
          placeholder="Enter email address"
          size="large"
          disabled={loading}
          data-testid="login-email-input"
        />
      </Form.Item>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        block
        loading={loading}
        data-testid="login-email-submit"
      >
        Sign In
      </Button>
    </Form>
  )
}

function EmailSent({ email, onBack }: { email: string; onBack: () => void }) {
  return (
    <Space
      direction="vertical"
      align="center"
      style={{ width: '100%', marginTop: 24 }}
      size="small"
      data-testid="login-email-sent"
    >
      <CheckCircleOutlined style={{ fontSize: 40, color: '#52c41a' }} />
      <Title level={4} style={{ margin: 0 }}>
        Check your inbox
      </Title>
      <Text type="secondary" style={{ textAlign: 'center' }}>
        We sent a sign-in link to <strong>{email}</strong>. Open it to sign in —
        the link works once.
      </Text>
      <Button
        type="link"
        size="small"
        onClick={onBack}
        data-testid="login-email-sent-back"
      >
        Use a different email or sign-in method
      </Button>
    </Space>
  )
}

export default function LoginPage() {
  const [loading, setLoading] = useState(false)
  const [sentTo, setSentTo] = useState<string | null>(null)

  const handleOAuthLogin = (method: OAuthLoginMethod) => {
    setLoading(true)
    authService.logout()
    window.location.href = oauthRedirectUrl(method)
  }

  const handleEmailSubmit = async (email: string) => {
    setLoading(true)
    authService.logout()
    try {
      const result = await requestPasswordlessLogin({
        email,
        redirectTo: callbackUrl(),
      })
      unwrapApiResult(result, 'Failed to send sign-in link')
      setSentTo(email)
    } catch (error) {
      console.error('Passwordless login request failed', error)
      void message.error(
        error instanceof Error
          ? error.message
          : 'Failed to send sign-in link. Please try again.',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'linear-gradient(to bottom, transparent 50%, rgba(84,79,255,0.3))',
        padding: 16,
      }}
    >
      <Card style={{ width: 400, maxWidth: '100%' }}>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
          Sign In
        </Title>

        {sentTo ? (
          <EmailSent email={sentTo} onBack={() => setSentTo(null)} />
        ) : (
          <>
            <OAuthButtons loading={loading} onLogin={handleOAuthLogin} />
            <Divider>or</Divider>
            <EmailLoginForm loading={loading} onSubmit={handleEmailSubmit} />
          </>
        )}
      </Card>
    </div>
  )
}
